// FE-SCRM-055 — Settings → Broker firm autocomplete defaults.
//
// Pin specific firm names so they surface at the top of
// ``/sales/broker-firms/suggestions``. Useful for the small number
// of firms a brokerage routinely competes against. Stored as a JSON
// array on ``BrokerSettings.pinned_broker_firms``.

import { useState } from "react";

import { Button, Input } from "@/design-system";

import type { BrokerSettings } from "../../settings/types";


interface Props {
  settings: BrokerSettings | null | undefined;
  onPatch: (input: Partial<BrokerSettings>) => void;
  isPending: boolean;
}


export function BrokerFirmsSection({
  settings, onPatch, isPending,
}: Props): JSX.Element {
  const pinned = settings?.pinned_broker_firms ?? [];
  const [draft, setDraft] = useState("");

  const addPinned = () => {
    const v = draft.trim();
    if (!v) return;
    if (pinned.some((p) => p.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    onPatch({ pinned_broker_firms: [...pinned, v] });
    setDraft("");
  };

  const removePinned = (name: string) => {
    onPatch({
      pinned_broker_firms: pinned.filter((p) => p !== name),
    });
  };

  return (
    <section
      data-testid="settings-broker-firms"
      className="flex flex-col gap-3"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-text-primary">
          Broker firm autocomplete defaults
        </h2>
        <p className="text-xs text-text-secondary">
          Pin firm names so they always appear at the top of the
          broker firm autocomplete on the Prospect form.
        </p>
      </header>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            label="Pin a firm"
            placeholder="e.g. Mercer"
            value={draft}
            disabled={isPending}
            onChange={(e) => setDraft(e.target.value)}
            data-testid="pinned-firm-input"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addPinned();
              }
            }}
          />
        </div>
        <Button
          variant="secondary"
          onClick={addPinned}
          disabled={!draft.trim() || isPending}
          data-testid="pinned-firm-add"
        >
          Add
        </Button>
      </div>

      {pinned.length === 0 ? (
        <p
          data-testid="pinned-firms-empty"
          className="text-xs text-text-tertiary"
        >
          No firms pinned. The autocomplete falls back to seeded
          firms + brokerage history.
        </p>
      ) : (
        <ul
          data-testid="pinned-firms-list"
          className="flex flex-wrap gap-2"
        >
          {pinned.map((name) => (
            <li
              key={name}
              data-testid={`pinned-firm-${name}`}
              className="flex items-center gap-2 rounded-full border border-border-subtle bg-recessed px-3 py-1 text-xs text-text-primary"
            >
              <span>{name}</span>
              <button
                type="button"
                aria-label={`Remove ${name}`}
                data-testid={`pinned-firm-remove-${name}`}
                onClick={() => removePinned(name)}
                disabled={isPending}
                className="text-text-tertiary hover:text-text-primary"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
